import { forwardRef, MutableRefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Eye, { EyeRef } from '@/components/Eye/Eye'
import UITitle from '@/components/UI/UITitle'
import { useAppState } from '@/composables/useAppState'
import { useGSAP } from '@/composables/useGSAP'
import { useImageFormatContext } from '@/composables/useImageFormatContext'
import useTracking from '@/composables/useTracking'
import { getContentfulUrl } from '@/utils/contentful'
import useMedia from '@/utils/hooks/useMedia'

import SummaryHeroEnterSwirl from './SummaryHeroEnterSwirl'
import SummaryHeroExitSwirl from './SummaryHeroExitSwirl'
import SummaryHeroPupil from './SummaryHeroPupil'

export type SummaryHeroProps = {
  className?: string
}

const SummaryHero = forwardRef<HTMLDivElement, SummaryHeroProps>(({ className }, sectionRef) => {
  const { t } = useTranslation()
  const { gsap } = useGSAP()
  const { trackClick } = useTracking()
  const { persona, state, actor, summary } = useAppState()
  const scenario = summary.scenario
  const isIntro = state.matches('summary.intro')
  const isReducedMotion = useMedia('(prefers-reduced-motion: reduce)')

  const sectionInnerRef = useRef<HTMLDivElement>(null)
  const eyeRef = useRef<EyeRef>(null)

  const [isEntering, setIsEntering] = useState<boolean>(true)
  const [showEnterSwirl, setShowEnterSwirl] = useState<boolean>(false)
  const [showExitSwirl, setShowExitSwirl] = useState<boolean>(false)
  const [showRunner, setShowRunner] = useState<boolean>(!isIntro)

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      if (isIntro) {
        gsap
          .timeline()
          .set(sectionInnerRef.current, { opacity: 0 }, 0)
          .set(sectionInnerRef.current, { opacity: 1, immediateRender: false }, 0.1)
          .add(() => setShowEnterSwirl(true), '0.5')
          .addLabel('start', 1)
          .add(() => eyeRef.current?.open({}), 'start')
          .add(() => setShowExitSwirl(true), 'start+=0.5')
          .add(() => setIsEntering(false), 'start+=2')
          .fromTo(
            sectionInnerRef.current.querySelectorAll('.SummaryHero__CTA'),
            {
              opacity: 0,
            },
            {
              opacity: 1,
              duration: 0.5,
              ease: 'quad.out',
            },
            'start+=1.5'
          )
      } else {
        gsap
          .timeline()
          .set(sectionInnerRef.current, { opacity: 0 }, 0)
          .set(sectionInnerRef.current, { opacity: 1, immediateRender: false }, 0.1)
          .addLabel('start', 0.1)
          .add(() => eyeRef.current?.open({}), 'start')
      }
    })

    return () => ctx.kill()
  }, [isIntro, eyeRef, sectionInnerRef, sectionRef, gsap, setIsEntering])

  const [isLeaving, setIsLeaving] = useState<boolean>(false)
  const leaveIntro = useCallback(() => {
    if (isLeaving) return

    if (isIntro && !isEntering) {
      setIsLeaving(true)

      trackClick({
        clickType: 'button',
        unitName: `${persona}_simulation_${summary.scenario}`,
        unitVariation: 'summary',
        unitLocation: 'tyce',
        buttonText: 'scroll_to_see_more',
      })

      const section = (sectionRef as MutableRefObject<HTMLDivElement>).current
      const tl = gsap.timeline()
      const mm = gsap.matchMedia()

      mm.add('(prefers-reduced-motion: no-preference)', () => {
        tl.add(() => eyeRef.current?.close({ blink: true }), 0)
        tl.add(() => setShowRunner(true), 0.75)
        tl.add(() => eyeRef.current?.open({}), 0.8)
      })

      tl.fromTo(
        section.querySelectorAll('.SummaryHero__Title .Line'),
        {
          yPercent: 0,
        },
        {
          yPercent: -150,
          duration: 0.75,
          ease: 'quart.inOut',
        },
        0
      )
        .fromTo(
          section.querySelector('.SummaryHero__CTA'),
          {
            opacity: 1,
          },
          {
            opacity: 0,
            duration: 0.75,
            ease: 'quart.inOut',
          },
          0
        )

        .add(() => actor.send({ type: 'NEXT' }), 0.8)
    }
  }, [sectionRef, isIntro, isEntering, isLeaving, actor, gsap, persona, trackClick, summary.scenario])

  useLayoutEffect(() => {
    const section = (sectionRef as MutableRefObject<HTMLDivElement>).current
    let hasScrolled = false
    const onScroll = () => {
      // console.log('scroll', { isIntro, hasScrolled, isEntering });
      if (isIntro && !hasScrolled && !isEntering) {
        hasScrolled = true
        leaveIntro()
      }
    }

    // console.log('Add scroll listener ?', { isIntro, isEntering, section });
    if (isIntro && !isEntering && section) {
      // console.log('Add scroll listener !');
      section.addEventListener('mousewheel', onScroll)
      section.addEventListener('DOMMouseScroll', onScroll)
      section.addEventListener('touchmove', onScroll)
    }

    return () => {
      section.removeEventListener('mousewheel', onScroll)
      section.removeEventListener('DOMMouseScroll', onScroll)
      section.removeEventListener('touchmove', onScroll)
    }
  }, [sectionRef, isIntro, isEntering, leaveIntro])

  const betterFormat = useImageFormatContext()
  const [imageUrl, setImageUrl] = useState(null)
  useEffect(() => {
    const url = getContentfulUrl(`/src/assets/images/summary/runner_${persona}_${scenario + 1}.jpg`, {
      fm: betterFormat,
    })
    setImageUrl(url)
  }, [persona, scenario, betterFormat])

  return (
    <section
      ref={sectionRef}
      className={`SummaryHero relative z-2 w-full ${isIntro ? '' : '-mb-[320px]'} ${className ? className : ''}`}
    >
      <div
        ref={sectionInnerRef}
        className='relative left-0 top-0 w-full min-h-screen md:h-screen flex flex-col items-center justify-center gap-0 z-2'
      >
        <div className='relative w-[115%] md:w-auto h-auto md:h-[75vh] aspect-350/207 md:aspect-1440/844 z-1'>
          {showExitSwirl && <SummaryHeroExitSwirl />}
          <Eye
            ref={eyeRef}
            className='w-full h-full'
            defaultOpen={!isIntro}
            childrenBackground={
              <>
                <div className='absolute top-0 left-0 w-full h-full bg-peach' />
              </>
            }
            childrenBetween={showEnterSwirl && <SummaryHeroEnterSwirl />}
            isSmallMobile
            //Deactivate the summary eye blink when reduced motion is preferred
            hasWaitingBlink={!isReducedMotion}
          >
            {isReducedMotion ? (
              <SummaryHeroPupil />
            ) : (
              <>
                {!showRunner && <SummaryHeroPupil />}
                <img
                  aria-hidden
                  src={imageUrl}
                  className={`absolute top-[9%] left-[9%] w-[82%] h-[82%] object-cover ${showRunner ? '' : 'opacity-0'}`}
                />
              </>
            )}
          </Eye>
        </div>
        <div className='-mt-[4.444vw] mb-90 w-full mt-2'>
          <UITitle
            is='h1'
            className={`SummaryHero__Title relative base-heading text-24 leading-tight z-2 w-full text-center ${
              isIntro ? '' : 'opacity-0'
            }`}
            i18nKey={`summary.title`}
            delay={2}
            appear={true}
            hasSideDecoration={false}
          />
          <UITitle
            is='h2'
            className={`SummaryHero__Title relative base-heading  text-heading-h2 leading-tight z-2 w-full text-center mt-12 ${
              isIntro ? '' : 'opacity-0'
            }`}
            i18nKey={`scenario.${persona}.${scenario + 1}.title`}
            delay={2}
            appear={true}
            hasSideDecoration={false}
          />
        </div>
        {isIntro && (
          <div className='absolute left-0 top-[100dvh] -translate-y-full md:-translate-y-0 md:top-auto md:bottom-0 w-full pointer-events-none flex items-center justify-center p-16 z-2'>
            <button
              className='SummaryHero__CTA bg-dark-cream rounded-12 min-h-40 flex items-center justify-center px-12 pb-2 text-body focus-visible:outline focus-visible:outline-2 outline-scarlet outline-offset-2'
              onClick={leaveIntro}
              type='button'
            >
              <span aria-hidden>{t('summary.scroll')}</span>
              <span className='sr-only'>{t('summary.scroll')}</span>
            </button>
          </div>
        )}
      </div>
    </section>
  )
})
export default SummaryHero
